import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Radio, Eye, EyeOff, Users, Activity, Zap, Loader2, Sparkles,
  TrendingUp, Target, Shield, ShieldOff, AlertTriangle, RefreshCw,
  BookOpen, ChevronDown, ChevronUp, Info,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getLiveFeed, getLiveFeedOptInStatus, setLiveFeedOptIn } from '../services/api';
import { formatUsd as fmtUsd, formatNumber as fmtNum } from '../services/displayPrefs';
import { InfoTip } from '../components/ui';

const EVENT_CONFIG = {
  'smart_trade.opened':       { emoji: '🟢', label: 'Smart Trade opened',   color: 'border-l-emerald-400', accent: 'text-emerald-400' },
  'smart_trade.tp_filled':    { emoji: '🎯', label: 'Take-profit filled',   color: 'border-l-emerald-400', accent: 'text-emerald-400' },
  'smart_trade.sl_filled':    { emoji: '🛑', label: 'Stop-loss filled',     color: 'border-l-red-400',     accent: 'text-red-400' },
  'smart_trade.closed':       { emoji: '⚪', label: 'Smart Trade closed',   color: 'border-l-gray-400',    accent: 'text-gray-400' },
  'grid_bot.deployed':        { emoji: '🚀', label: 'Grid Bot deployed',    color: 'border-l-cyan-400',    accent: 'text-cyan-400' },
  'grid_bot.fill':            { emoji: '📊', label: 'Grid fill',            color: 'border-l-emerald-400', accent: 'text-emerald-400' },
  'grid_bot.stopped':         { emoji: '⏹️', label: 'Grid Bot stopped',     color: 'border-l-gray-400',    accent: 'text-gray-400' },
  'dca_safety.so_filled':     { emoji: '💰', label: 'DCA safety filled',    color: 'border-l-violet-400',  accent: 'text-violet-400' },
  'dca_safety.tp_filled':     { emoji: '🎯', label: 'DCA take-profit',      color: 'border-l-emerald-400', accent: 'text-emerald-400' },
  'alert.fired':              { emoji: '🔔', label: 'Alert',                color: 'border-l-amber-400',   accent: 'text-amber-400' },
  'regime_switch':            { emoji: '🌐', label: 'Market regime switch', color: 'border-l-pink-400',    accent: 'text-pink-400' },
};

const TIER_COLOR = {
  trial:   'text-gray-400 bg-gray-500/10 border-gray-500/20',
  starter: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/20',
  trader:  'text-violet-400 bg-violet-500/10 border-violet-500/20',
  pro:     'text-amber-400 bg-amber-500/10 border-amber-500/20',
  elite:   'text-pink-400 bg-pink-500/10 border-pink-500/20',
};

function fmtRelative(ms) {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  if (diff < 0) return 'now';
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function fmtPrice(v) {
  if (v == null) return '';
  const n = Number(v);
  if (!Number.isFinite(n)) return '';
  // Routed through displayPrefs so the price reflects the user's
  // chosen display currency and number format.
  if (n >= 1) return fmtUsd(n, 2);
  return fmtUsd(n, 4);
}

// Render the event row's main message line from the event type + data
function eventLine(entry) {
  const d = entry.data || {};
  const sym = d.symbol || '';
  switch (entry.eventType) {
    case 'smart_trade.opened':
      return <>opened <span className="text-emerald-400">long</span>{d.side === 'short' ? <> <span className="text-red-400">short</span></> : ''} {sym}{d.entryPrice ? <> @ <span className="font-mono text-white">{fmtPrice(d.entryPrice)}</span></> : ''}</>;
    case 'smart_trade.tp_filled':
      return <>hit take-profit on {sym}{d.filledPrice ? <> @ <span className="font-mono text-emerald-400">{fmtPrice(d.filledPrice)}</span></> : ''}{d.pricePct ? <> (+{d.pricePct}%)</> : ''}</>;
    case 'smart_trade.sl_filled':
      return <>stopped out on {sym}{d.filledPrice ? <> @ <span className="font-mono text-red-400">{fmtPrice(d.filledPrice)}</span></> : ''}</>;
    case 'smart_trade.closed':
      return <>closed {sym}{d.closeReason ? <> <span className="text-gray-500">({d.closeReason})</span></> : ''}</>;
    case 'grid_bot.deployed':
      return <>deployed Grid Bot on {sym}{d.gridType ? <> <span className="text-gray-500">({d.gridType})</span></> : ''}</>;
    case 'grid_bot.fill':
      return <>grid fill on {sym} <span className={d.side === 'buy' ? 'text-emerald-400' : 'text-red-400'}>{(d.side || '').toUpperCase()}</span>{d.price ? <> @ <span className="font-mono text-white">{fmtPrice(d.price)}</span></> : ''}</>;
    case 'grid_bot.stopped':
      return <>stopped Grid Bot on {sym}</>;
    case 'dca_safety.so_filled':
      return <>DCA safety #{d.soIndex || '?'} filled on {sym}{d.price ? <> @ <span className="font-mono text-white">{fmtPrice(d.price)}</span></> : ''}</>;
    case 'dca_safety.tp_filled':
      return <>DCA take-profit on {sym}{d.price ? <> @ <span className="font-mono text-emerald-400">{fmtPrice(d.price)}</span></> : ''}{d.realizedPnl ? <> · <span className="font-mono text-emerald-400">+{fmtUsd(d.realizedPnl)}</span></> : ''}</>;
    case 'alert.fired':
      return <>alert fired{d.name ? <>: {d.name}</> : ''}{d.message ? <> — {d.message}</> : ''}</>;
    case 'regime_switch':
      return <>market regime{d.from && d.to ? <>: <span className="text-gray-400">{d.from}</span> → <span className="text-amber-400">{d.to}</span></> : ' shifted'}</>;
    default:
      return entry.eventType;
  }
}

export default function LiveFeedPage() {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [myStatus, setMyStatus] = useState(null);
  const [optInBusy, setOptInBusy] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [aboutOpen, setAboutOpen] = useState(true);
  const [optInError, setOptInError] = useState(null);
  const pollRef = useRef(null);

  async function load() {
    try {
      const res = await getLiveFeed({ limit: 50, event: filter });
      setEntries(res?.data?.entries || []);
      setStats(res?.data?.stats || null);
    } catch {} finally { setLoading(false); }
  }

  async function loadStatus() {
    try {
      const res = await getLiveFeedOptInStatus();
      const data = res?.data;
      setMyStatus(data);
      if (data?.displayName) setDisplayName(data.displayName);
    } catch {}
  }

  useEffect(() => {
    loadStatus();
    load();
  }, []);

  useEffect(() => {
    load();
  }, [filter]);

  // Auto-poll every 12 seconds for new events
  useEffect(() => {
    pollRef.current = setInterval(load, 12000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  async function handleToggleOptIn(enabled) {
    setOptInBusy(true);
    setOptInError(null);
    try {
      await setLiveFeedOptIn(enabled, enabled ? (displayName || null) : null);
      await loadStatus();
    } catch (err) {
      // Inline error banner instead of blocking alert(). Surfaces the
      // failure without freezing the page or leaking raw stack traces.
      setOptInError(err?.message || 'Could not update your opt-in. Please try again.');
    } finally { setOptInBusy(false); }
  }

  const eventTypeOptions = useMemo(() => {
    if (!stats?.breakdown) return [];
    return Object.entries(stats.breakdown)
      .sort((a, b) => b[1] - a[1])
      .map(([id, count]) => ({ id, count, ...EVENT_CONFIG[id] }));
  }, [stats]);

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-2xl border border-pink-500/20 bg-gradient-to-br from-pink-500/10 via-dark-800/40 to-dark-800/40 p-6">
        <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full bg-pink-400/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-16 -left-16 w-56 h-56 rounded-full bg-violet-400/5 blur-3xl pointer-events-none" />
        <div className="relative">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-violet-500 flex items-center justify-center shadow-lg shadow-pink-500/20">
              <Radio className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-2xl font-bold text-white">Live Trade Feed</h1>
                <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live
                </span>
              </div>
              <p className="text-xs text-pink-300/80 mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3 h-3" /> Opted-in users' real bot events · privacy-first · refreshes every 12s
                <InfoTip side="bottom">Privacy: only display name + tier + event + symbol exposed. Wallet / balance / position size NEVER public. NOT a buy signal — use as one input, not the only one. Cluster patterns (3+ publishers same symbol) = collective signal.</InfoTip>
              </p>
            </div>
          </div>

          {/* Stats band */}
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
              <HeroStat icon={Users}      label="Publishers"     value={stats.uniquePublishers} accent="text-pink-400" />
              <HeroStat icon={Activity}   label="Total events"   value={fmtNum(stats.totalEvents)} accent="text-violet-400" />
              <HeroStat icon={Zap}        label="Last 24h"       value={stats.eventsLast24h} accent="text-amber-400" />
              <HeroStat icon={TrendingUp} label="Now"            value={entries[0] ? fmtRelative(entries[0].ts) : '—'} accent="text-emerald-400" />
            </div>
          )}
        </div>
      </div>

      {/* ─── What is this tool? ──────────────────────────────────────────── */}
      <AboutCard open={aboutOpen} onToggle={() => setAboutOpen(o => !o)} />

      {/* ─── Opt-in card ────────────────────────────────────────────────── */}
      <OptInCard myStatus={myStatus} optInBusy={optInBusy} displayName={displayName} setDisplayName={setDisplayName} onToggle={handleToggleOptIn} />

      {optInError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-xs text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>{optInError}</span>
          <button onClick={() => setOptInError(null)} className="ml-auto text-red-300/70 hover:text-red-200 underline">Dismiss</button>
        </div>
      )}

      {/* ─── Event-type filter chips ──────────────────────────────────── */}
      {eventTypeOptions.length > 0 && (
        <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
          <FilterChip active={filter === null} onClick={() => setFilter(null)} label="All events" count={stats?.totalEvents} />
          {eventTypeOptions.map((opt) => (
            <FilterChip
              key={opt.id}
              active={filter === opt.id}
              onClick={() => setFilter(opt.id)}
              label={`${opt.emoji || ''} ${opt.label || opt.id}`.trim()}
              count={opt.count}
            />
          ))}
        </div>
      )}

      {/* ─── Feed ─────────────────────────────────────────────────────── */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-7 h-7 animate-spin text-pink-400" />
        </div>
      ) : entries.length === 0 ? (
        <EmptyState hasOptIn={myStatus?.optedIn} />
      ) : (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <FeedRow key={`${e.ts}-${i}`} entry={e} />
          ))}
        </div>
      )}

      <p className="text-[10px] text-gray-600 text-center">
        Showing the latest {entries.length} {filter ? 'filtered' : ''} events from opted-in users · refreshes every 12s · zero personal exchange data is exposed
      </p>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────────

function AboutCard({ open, onToggle }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 overflow-hidden">
      <button onClick={onToggle} className="w-full px-5 py-4 flex items-center justify-between hover:bg-dark-700/30 transition">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-pink-500/10 border border-pink-500/30 flex items-center justify-center">
            <BookOpen className="w-4 h-4 text-pink-400" />
          </div>
          <div className="text-left">
            <div className="text-sm font-semibold text-white">What is this feed — and how does it work?</div>
            <div className="text-[11px] text-gray-500">Plain-English walkthrough · 20 seconds</div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-dark-700/40 pt-4 space-y-4">
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What this is</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              A real-time stream of <span className="text-white">bot events from other haythix users who chose to publish</span>. Watch other traders' Grid Bots fill, Smart Trades hit take-profit, DCA safety orders trigger — all in real time, all from real users running real strategies. Refreshes every 12 seconds.
            </p>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What's NEVER shown</div>
            <ul className="text-sm text-gray-300 leading-relaxed space-y-1 list-disc pl-5">
              <li>Which exchange a user trades on</li>
              <li>Their account balance, position size, or PnL totals</li>
              <li>Their userId, email, or any personal identifier</li>
              <li>Their API keys, wallet addresses, or transaction hashes</li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-semibold text-pink-300 uppercase tracking-wider mb-1.5">What IS shown (when you opt in)</div>
            <p className="text-sm text-gray-300 leading-relaxed">
              Your chosen <span className="text-white">display name</span>, your <span className="text-white">tier badge</span>, the <span className="text-white">event type</span> (e.g. "grid fill"), the <span className="text-white">symbol</span> (e.g. ETH/USDC), and the <span className="text-white">price</span> of the event. That's it — enough for the community to see momentum, none of your personal data.
            </p>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/90 leading-relaxed">
              <span className="font-semibold">How to read this:</span> The feed shows events as they happen, not financial advice. Other users' wins don't predict your outcomes, and you have no way to know whether their underlying strategy fits your risk tolerance. Treat it as social proof of activity on the platform, not a copy-trading signal.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function HeroStat({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-dark-900/40 backdrop-blur-sm rounded-xl border border-dark-700/40 p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-3.5 h-3.5 ${accent || 'text-gray-400'}`} />
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">{label}</p>
      </div>
      <p className={`text-xl font-bold font-mono ${accent || 'text-white'}`}>{value}</p>
    </div>
  );
}

function FilterChip({ active, onClick, label, count }) {
  return (
    <button
      onClick={onClick}
      className={`text-xs px-3 py-1.5 rounded-full border whitespace-nowrap transition-all flex-shrink-0 flex items-center gap-1.5 ${
        active
          ? 'bg-pink-500/15 border-pink-400/40 text-pink-300'
          : 'bg-dark-800/40 border-dark-700/50 text-gray-400 hover:border-dark-600 hover:text-white'
      }`}
    >
      <span>{label}</span>
      {count != null && (
        <span className={`text-[9px] font-mono px-1 rounded ${active ? 'bg-pink-500/20' : 'bg-dark-700/60'}`}>{count}</span>
      )}
    </button>
  );
}

function OptInCard({ myStatus, optInBusy, displayName, setDisplayName, onToggle }) {
  if (!myStatus) return null;
  const optedIn = !!myStatus.optedIn;
  return (
    <div
      className={`rounded-2xl border p-4 transition-all ${
        optedIn
          ? 'bg-emerald-500/5 border-emerald-500/30'
          : 'bg-dark-800/40 border-dark-700/50'
      }`}
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 flex-1 min-w-[260px]">
          <div
            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
              optedIn ? 'bg-emerald-500/15 text-emerald-400' : 'bg-dark-700 text-gray-500'
            }`}
          >
            {optedIn ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">
              {optedIn ? 'Your bot events appear in the feed' : 'You are NOT publishing to the feed'}
            </p>
            <p className="text-xs text-gray-400 mt-1 leading-relaxed">
              {optedIn
                ? 'Other users see your display name + event type when your bots fire. Your exchange identity, balance, and userId are never exposed.'
                : 'Opt in to show your bot fires in the live feed. Your display name + tier are visible — your exchange and personal data stay private.'}
            </p>
            {optedIn && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value.slice(0, 32))}
                  placeholder="Display name (optional)"
                  maxLength={32}
                  className="bg-dark-700 border border-dark-600 rounded-lg p-1.5 text-white text-xs w-52"
                />
                <button
                  onClick={() => onToggle(true)}
                  disabled={optInBusy}
                  className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20"
                >
                  Save name
                </button>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => onToggle(!optedIn)}
          disabled={optInBusy}
          className={`text-xs px-4 py-2 rounded-lg font-semibold transition-all flex items-center gap-1.5 ${
            optedIn
              ? 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'
              : 'bg-gradient-to-r from-emerald-500 to-teal-400 text-dark-900 hover:from-emerald-400 hover:to-teal-300 shadow-lg shadow-emerald-500/20'
          } disabled:opacity-50`}
        >
          {optInBusy ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : optedIn ? (
            <><ShieldOff className="w-3 h-3" /> Opt out</>
          ) : (
            <><Shield className="w-3 h-3" /> Opt in</>
          )}
        </button>
      </div>
    </div>
  );
}

function EmptyState({ hasOptIn }) {
  return (
    <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-12 text-center">
      <div className="w-16 h-16 rounded-2xl bg-dark-700 mx-auto mb-4 flex items-center justify-center">
        <Radio className="w-8 h-8 text-gray-600" />
      </div>
      <p className="text-white font-medium mb-1">The feed is quiet right now</p>
      <p className="text-sm text-gray-500 max-w-md mx-auto">
        Bot events from opted-in users will appear here in real time. {hasOptIn ? "You're opted in — when your bots fire, they'll show up." : "Opt in above to start sharing your bot fires with the community."}
      </p>
    </div>
  );
}

function FeedRow({ entry }) {
  const cfg = EVENT_CONFIG[entry.eventType] || { emoji: '⚡', color: 'border-l-gray-400', accent: 'text-gray-400' };
  const tierStyle = TIER_COLOR[entry.tier] || TIER_COLOR.starter;
  const initial = (entry.displayName || '?').slice(0, 1).toUpperCase();
  return (
    <div className={`bg-dark-800/40 rounded-xl border border-dark-700/50 border-l-2 ${cfg.color} p-3 flex items-start gap-3 hover:bg-dark-800/60 transition-colors`}>
      {/* Avatar */}
      {entry.avatarUrl ? (
        <img src={entry.avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" />
      ) : (
        <div className="w-9 h-9 rounded-full bg-dark-700 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
          {initial}
        </div>
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          <span className="text-sm font-bold text-white truncate">{entry.displayName}</span>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded capitalize ${tierStyle}`}>{entry.tier}</span>
          <span className="text-[10px] text-gray-600 ml-auto">{fmtRelative(entry.ts)}</span>
        </div>
        <p className="text-sm text-gray-300">
          <span className={`mr-1 ${cfg.accent}`}>{cfg.emoji}</span>
          {eventLine(entry)}
        </p>
      </div>
    </div>
  );
}
